import type { PullRequestState, PullRequestSummary } from '@crowdex/workspace-protocol';
import {
  failedPullRequestChecks,
  type PullRequestCockpit,
  pendingPullRequestChecks,
} from '@crowdex/workspace-protocol/pull-request-cockpit';

export function pullRequestLabel(state: PullRequestState): string {
  if (state === 'draft') return 'Draft PR';
  if (state === 'merged') return 'Merged';
  if (state === 'closed') return 'Closed';
  return 'Open PR';
}

export function pullRequestStateClass(state: PullRequestState): string {
  if (state === 'merged') return 'text-mode-team';
  if (state === 'closed') return 'text-destructive';
  if (state === 'open') return 'text-desktop-success';
  return 'text-muted-foreground';
}

/** The live pull request, or the one git status last saw when it is still the branch's. */
export function pullRequestForBranch(
  live: PullRequestSummary | null,
  cached: PullRequestSummary | null | undefined,
  branch: string | null,
): PullRequestSummary | null {
  if (live !== null) return live;
  if (cached === null || cached === undefined || branch === null) return null;
  return cached.headBranch.trim() === branch.trim() ? cached : null;
}

/** Overall status tone derived from a cockpit's CI checks. */
type ChecksTone = 'failed' | 'pending' | 'success';

export type PullRequestChecksSummary = {
  failed: number;
  pending: number;
  tone: ChecksTone;
  label: string;
};

/** Summarizes a cockpit's CI checks into counts, an overall tone and a display label. */
export function summarizePullRequestChecks(cockpit: PullRequestCockpit): PullRequestChecksSummary {
  const failed = failedPullRequestChecks(cockpit).length;
  const pending = pendingPullRequestChecks(cockpit).length;
  const tone: ChecksTone = failed > 0 ? 'failed' : pending > 0 ? 'pending' : 'success';
  const label =
    failed > 0
      ? `${failed} failing ${failed === 1 ? 'check' : 'checks'}`
      : pending > 0
        ? `${pending} pending ${pending === 1 ? 'check' : 'checks'}`
        : cockpit.checks.length === 0
          ? 'No CI checks'
          : 'Checks successful';
  return { failed, pending, tone, label };
}

export function checksToneClass(tone: ChecksTone | undefined): string {
  return tone === 'failed'
    ? 'text-destructive'
    : tone === 'pending'
      ? 'text-running'
      : 'text-desktop-success';
}
